import { type FC, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import { useFooterButtonFunctions } from '@/features/selectionsPanel/hooks/useFooterButtonFunctions';
import { useGenericSelectionPanelFunctions } from '@/features/selectionsPanel/hooks/useGenericSelectionPanelFunctions';
import { useSelectionsPanelContext } from '@/features/selectionsPanel/selectionsPanelProvider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';

type Props = {
  footerHeightInPx?: number;
};

export const SelectionsPanelFooter: FC<Props> = ({
  footerHeightInPx = undefined,
}) => {
  const { onSaveSelectionsClick, getSaveButtonTooltip } =
    useFooterButtonFunctions();
  const { deselectAll } = useGenericSelectionPanelFunctions();
  const { activeFile, clearActiveFileWithToast } = useFileManagementContext();
  const { totalSelectionCount } = useSelectionsPanelContext();

  const handleOnDeselectAll = useCallback(
    (e) => {
      clearActiveFileWithToast();
      deselectAll(e);
    },
    [deselectAll, clearActiveFileWithToast]
  );

  return (
    <div
      className={twMerge('sticky bottom-0 flex w-full space-x-3 bg-white p-3')}
      style={{
        maxHeight: footerHeightInPx ? `${footerHeightInPx}px` : undefined,
        minHeight: footerHeightInPx ? `${footerHeightInPx}px` : undefined,
      }}
    >
      {!activeFile ? (
        <>
          <Button
            className="h-8 w-full px-3 py-2"
            data-tooltip-id={GLOBAL_TOOLTIP}
            disabled={!totalSelectionCount}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.SECONDARY}
            onClick={handleOnDeselectAll}
          >
            <Typography variant="button2">Clear</Typography>
          </Button>
          <Button
            className="h-8 w-full px-3 py-2"
            data-tooltip-content={getSaveButtonTooltip()}
            data-tooltip-id={GLOBAL_TOOLTIP}
            disabled={!totalSelectionCount}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            onClick={onSaveSelectionsClick}
          >
            <Typography variant="button2">Save</Typography>
          </Button>
        </>
      ) : (
        <Button
          className="h-8 w-full"
          disabled={!totalSelectionCount}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          onClick={handleOnDeselectAll}
        >
          <Typography variant="button2">Close</Typography>
        </Button>
      )}
    </div>
  );
};
